/**
 * The support ticket queue's market model (W2-CONSOLES, D364).
 *
 * `GET /v1/support/tickets` authorizes `support:view` **per market** and, without a `?market=`, runs un-scoped —
 * which under the fail-closed market RLS returns zero rows. The console asked for the queue with no market at all
 * (`apps/admin/src/…` → `CaseAdminApi.ticketQueue({ limit })`), so the queue was empty for every operator and a
 * `403` for an atendente whose grant is one city. The market must come from the operator's OWN grants — never the
 * platform market list — so a multi-city agent picks among its cities and a single-grant agent needs no picker.
 *
 * Pure and tested; the screen renders it. The server still authorizes the read.
 */
import { type OperatingMarketView } from '@vello/api-client';
import { marketsOfRegion } from './scope.js';
import { type ScopeState } from './shell.js';

export interface MarketChoice {
  readonly id: string;
  readonly name: string;
}

/**
 * The markets this operator may work the queue in: the cities inside its own authority, as the scope selector
 * already fused from `GET /v1/operator/grants` + the registered market catalog (`scopesFromGrantsAndMarkets`).
 * Sorted by display name; the catalog supplies the name, the grant supplies the authority.
 */
export function queueMarketChoices(scope: ScopeState, markets: readonly OperatingMarketView[]): readonly MarketChoice[] {
  const name = new Map(markets.map((m) => [m.id, m.name]));
  const inState = scope.current.region !== undefined && scope.current.city === undefined ? new Set(marketsOfRegion(scope.current.region, markets).map((m) => m.id)) : undefined;
  const ids = [...new Set(scope.allowed.map((s) => s.city).filter((c): c is string => c !== undefined))].filter((id) => inState === undefined || inState.has(id));
  return ids
    .map((id) => ({ id, name: name.get(id) ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The market the queue opens on: the city the operator is currently scoped to, else its only city, else none —
 * and with no market the screen must ask for one instead of sending an un-scoped read that answers empty.
 */
export function defaultQueueMarket(scope: ScopeState, markets: readonly OperatingMarketView[]): string | undefined {
  if (scope.current.city !== undefined) return scope.current.city;
  const choices = queueMarketChoices(scope, markets);
  return choices.length === 1 ? choices[0]?.id : undefined;
}

/** Whether the screen should offer a picker (more than one market is reachable). */
export function needsMarketPicker(scope: ScopeState, markets: readonly OperatingMarketView[]): boolean {
  return queueMarketChoices(scope, markets).length > 1;
}

/** What the queue panel shows, so the screen never renders an empty table that means three different things. */
export type QueueState = 'needs_market' | 'no_permission' | 'refused' | 'loading' | 'empty' | 'rows';

export function queueState(input: {
  readonly market: string | undefined;
  readonly mayView: boolean;
  readonly errorCode?: string;
  readonly rows?: readonly unknown[];
}): QueueState {
  if (!input.mayView) return 'no_permission';
  if (input.market === undefined || input.market === '') return 'needs_market';
  if (input.errorCode === 'FORBIDDEN' || input.errorCode === 'UNAUTHORIZED') return 'no_permission';
  if (input.errorCode !== undefined) return 'refused';
  if (input.rows === undefined) return 'loading';
  return input.rows.length === 0 ? 'empty' : 'rows';
}
